import * as Sentry from '@sentry/browser';
import { RESCUE_ATTR, isAssetRescueActive } from './assetRescueState';

// Ground-truth telemetry for a fired asset rescue.
//
// Why this exists: when the CDN is unreachable and the watchdog
// (assetRescueWatchdog.inline.js) rescues the boot from GCS, the user ends up
// working — slowly but fine. The post-login cdnReachabilityProbe therefore
// usually SUCCEEDS on that same session, so cdn_reachable:false is never
// emitted and the rescue is completely invisible. This event is the only
// reliable count of "CDN-unreachable session that the watchdog saved", which is
// exactly the population the rollout decision (specs/cdn-unreachable-gcs-fallback.md)
// hinges on, and the only eye in production where there is no QA browser.
//
// Fired from medsenderEntry — which is gated by claimPackExecution, so it
// evaluates once per boot — only when the rescue actually activated. Deduped to
// ONE event per session: a persistently-blocked tab re-boots continuously (the
// watchdog's sticky arm-delay drops to 0 after the first rescue) and each boot
// re-enters this path, so per-boot emission amplified a handful of stuck clients
// into tens of thousands of identical warnings (Sentry REACT-J4P: ~89% were
// same-session repeats). The rescued-*session* count is the signal the rollout
// and the permanent resilience layer care about, not rescued boots. Strictly
// best-effort: it must never throw or delay the rescued render.

// Bundle-owned per-session dedupe marker, set after captureMessage returns for
// the first rescue in a session. Its presence on entry means "a boot rescue was
// already submitted to the SDK this session" and we skip re-emitting.
//
// We deliberately do NOT read the watchdog's own sticky marker
// (medsender:asset-origin): the inline watchdog sets that SYNCHRONOUSLY, before
// it injects the GCS scripts and therefore before this bundle ever runs — so by
// the time we get here it is always present, even on the very first rescue, and
// would suppress every event including the first. This separate key is written
// only after a rescue is reported (we return early otherwise), so it gates the
// second and later boots of the session without swallowing the first.
const REPORTED_KEY = 'medsender:asset-rescue-reported';

const metaContent = (doc, name) => {
  try {
    return doc.querySelector(`meta[name="${name}"]`)?.getAttribute('content') || '';
  } catch {
    return '';
  }
};

// Whether a boot rescue was already reported earlier this session. Private-mode
// / storage-disabled sessions can't read the marker, so every boot reads as the
// first and reverts to per-boot emission — an accepted limitation (the
// rescued-session count is unaffected; only the storage-backed reboot loop that
// produced REACT-J4P's volume is de-duplicated).
const alreadyReported = (win) => {
  try {
    return win.sessionStorage.getItem(REPORTED_KEY) === 'true';
  } catch {
    return false;
  }
};

const rescuedTagCount = (doc) => {
  try {
    return doc.querySelectorAll(`[${RESCUE_ATTR}]`).length;
  } catch {
    return 0;
  }
};

export const reportAssetRescueActivation = ({
  sentry = Sentry,
  win = typeof window !== 'undefined' ? window : undefined,
  doc = typeof document !== 'undefined' ? document : undefined,
} = {}) => {
  try {
    if (!isAssetRescueActive() || !win || !doc) return false;
    if (alreadyReported(win)) return false;

    sentry.captureMessage('Asset rescue activated', {
      // Degraded-but-working: the user is served, just slowly and uncompressed.
      // Warning (not error) so it groups distinctly from the cdn_reachable:false
      // failure events while staying queryable for the rescued-population count.
      level: 'warning',
      // Sentry's default Dedupe integration ignores tags. Give each phase a
      // distinct identity so the later org-attributed event is not discarded as
      // a duplicate of this boot event when no other message occurs between them.
      fingerprint: ['asset-rescue', 'boot'],
      tags: {
        asset_rescue_active: true,
        // The boot-time signal: fires once per rescued session (including
        // sessions that throw before auth or never authenticate), but runs
        // before auth so it carries no org/user. Query phase:boot for the
        // rescued session count; group by org with the phase:authenticated
        // event below.
        phase: 'boot',
        deployment_type: metaContent(doc, 'm8-deployment-type') || 'stable',
      },
      extra: {
        rescued_tag_count: rescuedTagCount(doc),
        asset_rescue_base: metaContent(doc, 'm8-asset-rescue-base'),
      },
    });

    // Mark only after captureMessage returns without throwing, so a synchronous
    // capture error remains retryable on the next boot. The SDK reports
    // transport failures asynchronously; those do not keep this path retryable.
    try {
      win.sessionStorage.setItem(REPORTED_KEY, 'true');
    } catch {
      // Private mode: can't dedupe across boots; harmless (reverts to per-boot
      // emission for this storage-disabled session).
    }
    return true;
  } catch {
    // Telemetry must never affect the rescued boot.
    return false;
  }
};

// Org-attributed follow-up, emitted once per session from the post-auth flow
// (auth.js, alongside reportCdnReachabilityAfterAuth) once routing_org_id is
// known. The boot event above can't carry org because it runs at module load,
// before auth — so for rescued sessions that DO authenticate we re-emit with
// organization_id (and the Sentry user scope auth has set by then) so the
// rollout signal is groupable by affected org. Deduped per session so the
// several auth call sites don't multiply it. Best-effort; never throws.
const REPORTED_AFTER_AUTH_KEY = 'medsender:asset-rescue-reported-auth';

const alreadyReportedAfterAuth = (win) => {
  try {
    return win.sessionStorage.getItem(REPORTED_AFTER_AUTH_KEY) === 'true';
  } catch {
    return false;
  }
};

export const reportAssetRescueActivationAfterAuth = (organizationId, {
  sentry = Sentry,
  win = typeof window !== 'undefined' ? window : undefined,
  doc = typeof document !== 'undefined' ? document : undefined,
} = {}) => {
  try {
    if (!isAssetRescueActive() || !organizationId || !win || !doc) return false;
    if (alreadyReportedAfterAuth(win)) return false;

    sentry.captureMessage('Asset rescue activated', {
      level: 'warning',
      fingerprint: ['asset-rescue', 'authenticated'],
      tags: {
        asset_rescue_active: true,
        phase: 'authenticated',
        deployment_type: metaContent(doc, 'm8-deployment-type') || 'stable',
        organization_id: organizationId,
      },
      extra: {
        rescued_tag_count: rescuedTagCount(doc),
        asset_rescue_base: metaContent(doc, 'm8-asset-rescue-base'),
      },
    });

    // Mark only after captureMessage returns without throwing, so a synchronous
    // capture error remains retryable at the next post-auth call site. The SDK
    // reports transport failures asynchronously; those do not keep this path
    // retryable.
    try {
      win.sessionStorage.setItem(REPORTED_AFTER_AUTH_KEY, 'true');
    } catch {
      // Private mode: can't dedupe across calls; harmless (worst case a couple
      // of duplicate org-attributed events in one session).
    }
    return true;
  } catch {
    return false;
  }
};

export default reportAssetRescueActivation;
